import React, { useState, useMemo } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useNotes, useMonthlyNotesTotals } from '@/features/calendar/hooks/useNotes';
import { useInstructor } from '@/features/profile/hooks/useInstructor';
import { NotesMonthSlider } from './NotesMonthSlider';
import { NotesFilters } from './NotesFilters';
import { NoteCard } from './NoteCard';

interface IMyNotesTabProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

export const MyNotesTab: React.FC<IMyNotesTabProps> = ({ selectedDate, onDateChange }) => {
  const { t, currentLanguage } = useI18n();
  const { isInstructor } = useInstructor();
  const selectedMonth = selectedDate.getMonth() + 1;
  const selectedYear = selectedDate.getFullYear();

  const [noteType, setNoteType] = useState<'all' | 'personal' | 'class' | 'feedback'>('all');
  const [category, setCategory] = useState<string | null>(null);
  const [technique, setTechnique] = useState<string | null>(null);
  const [student, setStudent] = useState<string | null>(null);

  // Get monthly totals for ALL months (completely independent query)
  // IMPORTANT: Don't pass noteType or category to keep queryKey stable
  // The slider should always show ALL notes, regardless of filters
  const { monthlyTotals } = useMonthlyNotesTotals();

  // Get notes without technique filter to calculate available techniques and students
  const { notes: allNotesForTechniques, isLoading: isLoadingForTechniques } = useNotes({
    month: selectedMonth,
    year: selectedYear,
    noteType: noteType === 'all' ? undefined : noteType,
    category: category || undefined,
    studentId: student || undefined,
    // Don't filter by technique here - we need all notes to calculate available techniques
  });

  // Get final filtered notes (with technique filter)
  const { notes, isLoading } = useNotes({
    month: selectedMonth,
    year: selectedYear,
    noteType: noteType === 'all' ? undefined : noteType,
    category: category || undefined,
    technique: technique || undefined,
    studentId: student || undefined,
  });

  const handleMonthChange = (direction: 'prev' | 'next'): void => {
    const newDate = direction === 'prev' 
      ? subMonths(selectedDate, 1)
      : addMonths(selectedDate, 1);
    onDateChange(newDate);
  };

  const handleMonthSelect = (month: number, year: number): void => {
    onDateChange(new Date(year, month - 1, 1));
  };

  const handleNoteTypeChange = (type: 'all' | 'personal' | 'class' | 'feedback'): void => {
    setNoteType(type);
    setTechnique(null); // Reset technique when note type changes
  };

  const handleCategoryChange = (cat: string | null): void => {
    setCategory(cat);
    setTechnique(null); // Reset technique when category changes
  };

  return (
    <div>
      {/* Month Slider */}
      {monthlyTotals && monthlyTotals.length > 0 && (
        <div className="mb-4">
          <NotesMonthSlider
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthSelect={handleMonthSelect}
            monthlyTotals={monthlyTotals}
          />
        </div>
      )}

      {/* Monthly Navigation */}
      <div className="flex items-center justify-between mb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleMonthChange('prev')}
          className="text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-white">
          {(() => {
            const month = format(selectedDate, 'MMMM', { locale: currentLanguage === 'pt' ? ptBR : undefined });
            const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
            return t('calendar.month_year', { 
              month: capitalizedMonth, 
              year: format(selectedDate, 'yyyy') 
            });
          })()}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleMonthChange('next')}
          className="text-white hover:bg-white/10"
        >
          <ArrowRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Filters */}
      <NotesFilters
        noteType={noteType}
        category={category}
        technique={technique}
        student={student}
        isInstructor={isInstructor}
        onNoteTypeChange={handleNoteTypeChange}
        onCategoryChange={handleCategoryChange}
        onTechniqueChange={setTechnique}
        onStudentChange={setStudent}
        filteredNotes={allNotesForTechniques || []}
      />

      {/* Notes List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-lg text-white">{t('loading.notes')}</div>
        </div>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 bg-[#281B1B] rounded-full flex items-center justify-center mb-6">
            <svg 
              className="w-10 h-10 text-white/40" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-white mb-3">
            {t('calendar.my_notes.no_notes_found')}
          </h3>
          <p className="text-white/60 text-sm max-w-sm">
            {t('calendar.my_notes.no_notes_description')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} />
          ))}
        </div>
      )}
    </div>
  );
};

